import { DateTime } from 'luxon';
import { DateRangePreset, DateRangeTab, MONTHS } from '../constants';
import type { DateRangeValue } from '../DateRangeInput';

const NOON = { hour: 12, minute: 0, second: 0, millisecond: 0 };
const MIN_YEAR = 1000;
const MAX_YEAR = 9999;

const LOCAL_ZONE = 'local';
const INPUT_DATE_FORMAT = 'yyyy-MM-dd';

function now(): DateTime {
  return DateTime.now().setZone(LOCAL_ZONE);
}

function toDateOrNull(dt: DateTime): Date | null {
  if (!dt.isValid) return null;
  const year = dt.year;
  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  return dt.set(NOON).toJSDate();
}

function parseToDateTime(input: string | null | undefined): DateTime | null {
  if (input == null || String(input).trim() === '') return null;

  const str = String(input).trim();

  const dt = DateTime.fromFormat(str, INPUT_DATE_FORMAT, { zone: LOCAL_ZONE });
  if (dt.isValid) return dt;

  return null;
}

function normalizeToDateTime(value: Date | string): DateTime | null {
  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value, { zone: LOCAL_ZONE });
    return dt.isValid ? dt : null;
  }
  return parseToDateTime(value);
}

export const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const dt = DateTime.fromJSDate(date, { zone: LOCAL_ZONE });
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_SHORT) : '';
};

export const formatDateForInput = (date: Date | null): string => {
  if (!date) return '';
  const dt = DateTime.fromJSDate(date, { zone: LOCAL_ZONE });
  return dt.isValid ? dt.toFormat(INPUT_DATE_FORMAT) : '';
};

export const parseDate = (input: string | null | undefined): Date | null => {
  const dt = parseToDateTime(input);
  return dt ? toDateOrNull(dt) : null;
};

export const getYearRange = (year: number): DateRangeValue => {
  const start = DateTime.fromObject({ year, month: 1, day: 1, ...NOON }, { zone: LOCAL_ZONE });
  const end = DateTime.fromObject({ year, month: 12, day: 31, ...NOON }, { zone: LOCAL_ZONE });
  return { from: start.toJSDate(), to: end.toJSDate() };
};

export const getQuarterRange = (year: number, quarter: number): DateRangeValue => {
  const startMonth = (quarter - 1) * 3 + 1;
  const start = DateTime.fromObject({ year, month: startMonth, day: 1, ...NOON }, { zone: LOCAL_ZONE });
  const end = start.plus({ months: 2 }).endOf('month').set(NOON);
  return { from: start.toJSDate(), to: end.toJSDate() };
};

export const getMonthRange = (year: number, month0: number): DateRangeValue => {
  const start = DateTime.fromObject({ year, month: month0 + 1, day: 1, ...NOON }, { zone: LOCAL_ZONE });
  const end = start.endOf('month').set(NOON);
  return { from: start.toJSDate(), to: end.toJSDate() };
};

export const isSameDay = (d1: Date | string | null, d2: Date | string | null): boolean => {
  if (d1 === d2) return true;
  if (d1 == null || d2 == null) return false;

  const dt1 = normalizeToDateTime(d1);
  const dt2 = normalizeToDateTime(d2);
  return dt1 != null && dt2 != null && dt1.hasSame(dt2, 'day');
};

export const isSameRange = (r1: DateRangeValue, r2: DateRangeValue): boolean => {
  return isSameDay(r1.from, r2.from) && isSameDay(r1.to, r2.to);
};

export const getPresetRange = (preset: DateRangePreset): DateRangeValue => {
  const currentNow = now();

  switch (preset) {
    case DateRangePreset.ThisYear:
      return getYearRange(currentNow.year);
    case DateRangePreset.ThisQuarter:
      return getQuarterRange(currentNow.year, currentNow.quarter);
    case DateRangePreset.ThisMonth:
      return getMonthRange(currentNow.year, currentNow.month - 1);
    case DateRangePreset.LastYear:
      return getYearRange(currentNow.year - 1);
    case DateRangePreset.LastQuarter: {
      const lastQ = currentNow.minus({ quarters: 1 }).startOf('quarter');
      return getQuarterRange(lastQ.year, lastQ.quarter);
    }
    case DateRangePreset.LastMonth: {
      const lastM = currentNow.minus({ months: 1 }).startOf('month');
      return getMonthRange(lastM.year, lastM.month - 1);
    }
    case DateRangePreset.Custom:
    default:
      return { from: null, to: null };
  }
};

export const isValidParsedDate = (d: Date | null): d is Date => {
  if (!d) return false;
  const dt = DateTime.fromJSDate(d, { zone: LOCAL_ZONE });
  return dt.isValid && dt.year >= MIN_YEAR && dt.year <= MAX_YEAR;
};

export const getYearFromDate = (d: Date | string | null | undefined): number => {
  const fallbackYear = now().year;
  if (d == null) return fallbackYear;
  const dt = normalizeToDateTime(d);
  return dt?.year ?? fallbackYear;
};

export const getCurrentYear = (): number => {
  return now().year;
};

export const getCurrentQuarter = (): number => {
  return now().quarter;
};

function toLocalDateTime(date: Date | null): DateTime | null {
  if (date == null) return null;
  return normalizeToDateTime(date);
}

export function isFullYearRange(fromDate: DateTime, toDate: DateTime): boolean {
  return (
    fromDate.year === toDate.year &&
    fromDate.ordinal === 1 &&
    toDate.ordinal === toDate.daysInYear
  );
}

export function isFullMonthRange(fromDate: DateTime, toDate: DateTime): boolean {
  return (
    fromDate.year === toDate.year &&
    fromDate.month === toDate.month &&
    fromDate.day === 1 &&
    toDate.day === toDate.daysInMonth
  );
}

export function isFullQuarterRange(fromDate: DateTime, toDate: DateTime): boolean {
  if (fromDate.year !== toDate.year) return false;
  if (fromDate.day !== 1) return false;
  if ((fromDate.month - 1) % 3 !== 0) return false;

  const quarterEnd = fromDate.plus({ months: 2 }).endOf('month').set(NOON);
  return toDate.hasSame(quarterEnd, 'day');
}

export const determineRangeInfo = (
  range: DateRangeValue
): { tab: DateRangeTab; year: number } => {
  const fromDate = toLocalDateTime(range.from);
  const toDate = toLocalDateTime(range.to);

  if (fromDate == null || toDate == null) {
    return { tab: DateRangeTab.Custom, year: getYearFromDate(range.from ?? range.to) };
  }

  const year = fromDate.year;

  if (isFullYearRange(fromDate, toDate)) {
    return { tab: DateRangeTab.Year, year };
  }

  if (isFullMonthRange(fromDate, toDate)) {
    return { tab: DateRangeTab.Month, year };
  }

  if (isFullQuarterRange(fromDate, toDate)) {
    return { tab: DateRangeTab.Quarter, year };
  }

  return { tab: DateRangeTab.Custom, year: getYearFromDate(range.from) };
};

export const formatPeriodLabel = (from: string, to: string): string => {
  const fromDate = parseToDateTime(from);
  const toDate = parseToDateTime(to);

  if (fromDate == null || toDate == null) return '';

  if (isFullYearRange(fromDate, toDate)) return String(fromDate.year);
  if (isFullQuarterRange(fromDate, toDate)) return `Q${fromDate.quarter} ${fromDate.year}`;
  if (isFullMonthRange(fromDate, toDate)) return `${MONTHS[fromDate.month - 1].name} ${fromDate.year}`;

  return `${formatDate(toDateOrNull(fromDate))} – ${formatDate(toDateOrNull(toDate))}`;
};
